import { useContext, useEffect, useMemo } from 'react';
import { formatPhoneNumber, useMedicalCardInfo, useProfileImage } from 'all-in-one-shared-api';

import { getCurrentDateString } from 'utils';
import { useKeycloakReady } from 'hooks/useKeycloakReady';

import { MedicalCardSection } from './components';
import { MedicalCardSectionData } from './interfaces';
import { HomeContext } from 'contexts/home/context';

const MedicalCardPage = (): JSX.Element => {
  const { ready, token } = useKeycloakReady();

  const { state } = useContext(HomeContext);
  const { userAccess } = state;
  const { allowedToViewCard } = userAccess ?? {};

  const currentDate = getCurrentDateString('DD-MM-YYYY');

  const { shouldFetch, data } = useMedicalCardInfo({ name: 'useMedicalCardInfo' }, token ?? '', {
    suspense: true,
  });

  const memberTypes = useMemo(() => {
    if (!data) return '';

    const types = data.map((elem) => elem.memberType);
    return types ? types.join(',') : '';
  }, [data]);

  const { shouldFetch: profileImageShouldFetch, data: profileImageData } = useProfileImage(
    { name: 'useProfileImage', params: { memberTypes } },
    token ?? '',
    memberTypes,
  );

  const medicalCardInfo = useMemo<MedicalCardSectionData[]>(() => {
    if (data) {
      return data.map((elem) => ({
        ...elem,
        // AIOBRB-1222: `lastName` first
        name: [elem.lastName, elem.firstName].filter((n) => !!n).join(' '),
        generatedOn: currentDate,
        certNo: elem?.memberId && elem?.memberType ? `${elem.memberId}-${elem.memberType}` : '',
        avatarUrl: profileImageData?.find((imageData) => imageData.memberType === elem.memberType)?.pictureUrl,
        serviceProviderHotline: formatPhoneNumber(elem.serviceProviderHotline || ''),
      }));
    }

    return [];
  }, [currentDate, data, profileImageData]);

  useEffect(() => {
    shouldFetch(!!ready);
  }, [ready, shouldFetch]);

  useEffect(() => {
    profileImageShouldFetch(!!ready && !!memberTypes);
  }, [ready, profileImageShouldFetch, memberTypes]);

  // Note(Andy Chow): To handle the case that users access /medical-card directly without access right
  // At the moment we don't have the design when the users are disallowed to view medical card, so simply display nothing
  if (!allowedToViewCard) {
    return <></>;
  }

  return (
    <>
      {medicalCardInfo.map((elem, index) => (
        <MedicalCardSection key={index} {...elem} />
      ))}
    </>
  );
};

export default MedicalCardPage;
